import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { providerDrawerSections } from '@common/constants';
import {
  useCreateProvider,
  useGetCountriesList,
  useGetProviderLovsList,
  useGetTitlesList,
  useGetZonesList
} from '@common/slices';
import { ExistingProviderLookups, NewProviderLookups } from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { useDynamicFormDrawerStyles } from '@components/dynamic-form/style';
import Loader from '@components/loader/Loader';
import { RootState } from '@core/redux/store';
import { enumToList, lookupToList } from '@utils';

import { useFormik } from 'formik';
import * as Yup from 'yup';

interface Props {
  onSuccess: (id: string) => void;
  data?: {
    fullName: string;
    dateOfBirth: string;
    email: string;
    phoneType: string;
    phoneNumber: string;
    isTaxable: boolean;
    type: string;
    isCompany: string;
    isLocal: string;
    contractStartDate: string;
    contractEndDate: string;
  };
}

enum ProviderStatusList {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING'
}

enum PhoneTypeList {
  MOBILE = 'MOBILE',
  LANDLINE = 'LANDLINE'
}

const ProviderDrawer: React.FC<Props> = ({ onSuccess }: Props) => {
  const buttonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );

  const { classes } = useDynamicFormDrawerStyles();

  const { data: titles } = useGetTitlesList();
  const { data: countries } = useGetCountriesList();
  const { data: zones } = useGetZonesList();
  const { data: providerLovs } = useGetProviderLovsList();
  // const { data: activePersons } = useGetActivePersonsList();
  const [createProvider] = useCreateProvider();

  const [lookups, setLookups] = useState<
    NewProviderLookups | ExistingProviderLookups
  >(null);

  const providerFormFormik = useFormik({
    initialValues: {
      type: '',
      isCompany: false,
      isLocal: false,
      contractStartDate: '',
      contractEndDate: '',
      status: '',
      newPerson: false,
      personId: '',
      address: {
        country: null,
        zone: null,
        city: null,
        street: null,
        building: null,
        addressDetails: null,
        addressDetailsArabic: null
      },
      addressType: null,
      title: null,
      firstName: null,
      lastName: null,
      middleName: null,
      dateOfBirth: null,
      email: null,
      phoneType: null,
      phoneNumber: null,
      isTaxable: false,
      auxiliary: null,
      taxAuxiliary: null
    },
    validationSchema: Yup.object({
      type: Yup.string().required('Required'),
      isCompany: Yup.boolean().notRequired(),
      isLocal: Yup.boolean().notRequired(),
      contractStartDate: Yup.date().required('Required'),
      status: Yup.mixed<ProviderStatusList>()
        .oneOf(Object.values(ProviderStatusList), 'Invalid status')
        .required('Required'),
      contractEndDate: Yup.date()
        .when('status', {
          is: (val: string) => val?.toUpperCase() === 'INACTIVE',
          then: (schema) => schema.required('Required').max(new Date()),
          otherwise: (schema) => schema.notRequired()
        })
        .nullable(),
      newPerson: Yup.boolean(),
      personId: Yup.string().when('newPerson', {
        is: (val: boolean) => val,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) => schema.required('Required')
      }),
      addressType: Yup.string().when('newPerson', {
        is: (val: boolean) => !val,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) => schema.required('Required')
      }),
      address: Yup.object({
        country: Yup.string().when('newPerson', {
          is: (val: boolean) => !val,
          then: (schema) => schema.nullable().notRequired(),
          otherwise: (schema) => schema.required('Required')
        }),
        city: Yup.string().when('newPerson', {
          is: (val: boolean) => !val,
          then: (schema) => schema.nullable().notRequired(),
          otherwise: (schema) => schema.required('Required')
        }),
        zone: Yup.string().notRequired(),
        street: Yup.string().notRequired(),
        building: Yup.string().notRequired(),
        addressDetails: Yup.string().notRequired(),
        addressDetailsArabic: Yup.string().notRequired()
      }).when('newPerson', {
        is: (val: boolean) => !val,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) => schema.required('Required')
      }),
      title: Yup.string().when('newPerson', {
        is: (val: boolean) => !val,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) => schema.required('Required')
      }),
      firstName: Yup.string().when('newPerson', {
        is: (val: boolean) => !val,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) => schema.required('Required')
      }),
      lastName: Yup.string().when('newPerson', {
        is: (val: boolean) => !val,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) => schema.required('Required')
      }),
      middleName: Yup.string().when('newPerson', {
        is: (val: boolean) => !val,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) => schema.required('Required')
      }),
      dateOfBirth: Yup.date()
        .max(new Date())
        .when('newPerson', {
          is: (val: boolean) => !val,
          then: (schema) => schema.nullable().notRequired(),
          otherwise: (schema) => schema.required('Required')
        }),
      email: Yup.string()
        .email()
        .when('newPerson', {
          is: (val: boolean) => !val,
          then: (schema) => schema.nullable().notRequired(),
          otherwise: (schema) => schema.required('Required')
        }),
      phoneType: Yup.mixed<PhoneTypeList>()
        .oneOf(Object.values(PhoneTypeList), 'Invalid phone type')
        .when('newPerson', {
          is: (val: boolean) => !val,
          then: (schema) => schema.nullable().notRequired(),
          otherwise: (schema) => schema.required('Required')
        }),
      phoneNumber: Yup.string().when('newPerson', {
        is: (val: boolean) => !val,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) => schema.required('Required')
      }),
      isTaxable: Yup.boolean().when('newPerson', {
        is: (val: boolean) => !val,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) => schema.required('Required')
      }),
      auxiliary: Yup.string()
        .required('Required')
        .length(8, 'Auxiliary should be 8 characters'),
      taxAuxiliary: Yup.string()
        .required('Required')
        .length(8, 'Tax Auxiliary should be 8 characters')
    }),
    onSubmit: async (values) => {
      await createProvider({
        createProviderInput: {
          ...values,
          auxiliary: values.auxiliary || null,
          taxAuxiliary: values.taxAuxiliary || null
        }
      }).then((data) => {
        if (data.data)
          setTimeout(() => onSuccess(data.data.salesforce.createProvider), 500);
      });
    }
  });

  const handleChange = async (
    fieldName: string,
    value: string | number | boolean
  ) => {
    if (fieldName === 'personId') {
      const pers = providerLovs.salesforce.getActivePersonsNonProvider.find(
        (person) => person.id === value
      );
      providerFormFormik.setValues({
        ...providerFormFormik.values,
        email: pers?.email,
        personId: value as string,
        phoneType: pers?.phoneDetails?.phoneType,
        phoneNumber: pers?.phoneDetails?.phoneNumber,
        isTaxable: pers?.isTaxable
      });
    } else if (fieldName === 'newPerson') {
      providerFormFormik.setValues({
        ...providerFormFormik.values,
        newPerson: value as boolean,
        email: '',
        personId: '',
        phoneType: '',
        phoneNumber: '',
        isTaxable: false
      });
    } else if (fieldName === 'dateOfBirth') {
      const d = new Date(value as string);

      const date = new Date(
        Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0)
      ).toISOString();
      providerFormFormik.setValues({
        ...providerFormFormik.values,
        dateOfBirth: date
      });
    } else {
      providerFormFormik.setFieldValue(fieldName, value);
    }
    providerFormFormik.setFieldTouched(fieldName, true);
  };

  const loadLookups = useCallback(async () => {
    let listOfValues;
    if (providerFormFormik.values.newPerson) {
      listOfValues = {
        titles: lookupToList(titles?.core?.getTitles),
        phoneType: enumToList(providerLovs?.phoneType?.enumValues),
        addressType: enumToList(providerLovs?.personAddressType?.enumValues),
        countries: lookupToList(countries?.core?.getCountries),
        zones: lookupToList(zones?.core?.getZones),
        providerTypes: lookupToList(providerLovs?.salesforce?.getProviderTypes),
        status: enumToList(providerLovs?.providerStatusList?.enumValues)
      };
    } else {
      listOfValues = {
        phoneType: enumToList(providerLovs?.phoneType?.enumValues),
        persons:
          providerLovs?.salesforce?.getActivePersonsNonProvider?.reduce(
            (acc, el) => {
              acc[el.id] = el.fullName;

              return acc;
            },
            {} as Record<string, string>
          ) || {},
        providerTypes: lookupToList(providerLovs?.salesforce?.getProviderTypes),
        status: enumToList(providerLovs?.providerStatusList?.enumValues)
      };
    }

    setLookups(listOfValues);
  }, [
    providerFormFormik.values.newPerson,
    titles,
    countries,
    zones,
    providerLovs
  ]);

  useEffect(() => {
    loadLookups();
  }, [
    providerFormFormik.values.newPerson,
    titles,
    countries,
    zones,
    providerLovs
  ]);

  return (
    <>
      {!lookups ? (
        <div className={classes.loaderContainer}>
          <Loader />
        </div>
      ) : (
        // Catch any bubbling submit from inside the drawer
        <div>
          <DynamicFormWidget
            onChange={handleChange}
            sections={providerDrawerSections(providerFormFormik, lookups)}
            disableForm={false}
            onSubmit={() => providerFormFormik.handleSubmit()}
            submitButtonState={buttonState}
          />
        </div>
      )}
    </>
  );
};

export { ProviderDrawer };
